import React, { useEffect, useMemo, useRef, useState } from 'react';
import AddOutlined from '@mui/icons-material/AddOutlined';
import CalendarMonthOutlined from '@mui/icons-material/CalendarMonthOutlined';
import EventBusyOutlined from '@mui/icons-material/EventBusyOutlined';
import { fetchRecruiterHome } from '../services/roomService';
import RecentMeetingsSection from './history/RecentMeetingsSection';
import MeetGeekLogo from './icons/MeetGeekLogo';
import './LandingPage.css';

const WEEKDAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

function startOfDay(date) {
  const next = new Date(date);
  next.setHours(0, 0, 0, 0);
  return next;
}

function startOfWeek(date) {
  const next = startOfDay(date);
  next.setDate(next.getDate() - next.getDay());
  return next;
}

function addDays(date, days) {
  const next = new Date(date);
  next.setDate(next.getDate() + days);
  return next;
}

function sameDay(a, b) {
  return (
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate()
  );
}

function formatDayHeading(date) {
  const weekday = date.toLocaleDateString(undefined, { weekday: 'short' });
  const month = date.toLocaleDateString(undefined, { month: 'short' });
  return `${weekday} ${date.getDate()} ${month}`;
}

function formatMonthHeading(date) {
  return date.toLocaleDateString(undefined, { month: 'long', year: 'numeric' });
}

function startOfMonth(date) {
  return new Date(date.getFullYear(), date.getMonth(), 1);
}

function addMonths(date, count) {
  return new Date(date.getFullYear(), date.getMonth() + count, 1);
}

function monthCells(monthDate) {
  const first = startOfMonth(monthDate);
  const startOffset = (first.getDay() + 6) % 7;
  const gridStart = addDays(first, -startOffset);
  return Array.from({ length: 42 }, (_, index) => addDays(gridStart, index));
}

function formatTimeRange(start, end) {
  const options = { hour: 'numeric', minute: '2-digit', hour12: true };
  const startLabel = start
    ? new Date(start).toLocaleTimeString(undefined, options)
    : '';
  const endLabel = end
    ? new Date(end).toLocaleTimeString(undefined, options)
    : '';
  if (startLabel && endLabel) return `${startLabel} – ${endLabel}`;
  return startLabel || endLabel;
}

/** past | live | upcoming — based on wall clock vs event window */
function getEventTimingStatus(event, now = new Date()) {
  const start = event?.start ? new Date(event.start) : null;
  const end = event?.end ? new Date(event.end) : null;
  if (!start || Number.isNaN(start.getTime())) return 'upcoming';

  const endMs = end && !Number.isNaN(end.getTime())
    ? end.getTime()
    : start.getTime() + 60 * 60 * 1000;
  const nowMs = now.getTime();

  if (nowMs >= endMs) return 'past';
  if (nowMs >= start.getTime()) return 'live';
  return 'upcoming';
}

const EVENT_STATUS_LABEL = {
  past: 'Ended',
  live: 'Now',
  upcoming: 'Upcoming',
};

function VideoIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <rect x="3" y="6" width="12" height="12" rx="2" stroke="currentColor" strokeWidth="1.8" />
      <path d="M15 10.5L20 8V16L15 13.5V10.5Z" stroke="currentColor" strokeWidth="1.8" strokeLinejoin="round" />
    </svg>
  );
}

function CalendarIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <rect x="3.5" y="5" width="17" height="15" rx="2" stroke="currentColor" strokeWidth="1.7" />
      <path d="M8 3.5V7M16 3.5V7M3.5 10H20.5" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" />
    </svg>
  );
}

function EmptyIllustration() {
  return (
    <div className="mg-empty-art" aria-hidden="true">
      <EventBusyOutlined />
    </div>
  );
}

export default function LandingPage({
  recruiterId,
  companyId,
  recruiterName,
  joinCode,
  onJoinCodeChange,
  onJoin,
  onStartInstant,
  onScheduleLater,
  onJoinEvent,
  onViewMeetingHistory,
  onOpenHistoryMeeting,
  onResolvedName,
  isCreatingMeeting,
  isJoining,
  refreshKey,
}) {
  const [selectedDate, setSelectedDate] = useState(() => startOfDay(new Date()));
  const [weekStart, setWeekStart] = useState(() => startOfWeek(new Date()));
  const [menuOpen, setMenuOpen] = useState(false);
  const [menuAnchor, setMenuAnchor] = useState('top');
  const [calendarOpen, setCalendarOpen] = useState(false);
  const [visibleMonth, setVisibleMonth] = useState(() => startOfMonth(new Date()));
  const [draftName, setDraftName] = useState('');
  const [recruiter, setRecruiter] = useState(null);
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [now, setNow] = useState(() => new Date());
  const topMenuRef = useRef(null);
  const bottomMenuRef = useRef(null);
  const calendarRef = useRef(null);

  const weekDays = useMemo(
    () => Array.from({ length: 7 }, (_, index) => addDays(weekStart, index)),
    [weekStart]
  );
  const today = startOfDay(now);
  const isPastDay = selectedDate < today;
  const canFetch = Boolean(recruiterId && companyId);

  useEffect(() => {
    const tick = () => setNow(new Date());
    const id = window.setInterval(tick, 30_000);
    return () => window.clearInterval(id);
  }, []);

  useEffect(() => {
    if (!canFetch) {
      setRecruiter(null);
      setEvents([]);
      setError('');
      setLoading(false);
      return undefined;
    }

    let cancelled = false;
    const load = async () => {
      setLoading(true);
      setError('');
      try {
        const data = await fetchRecruiterHome({
          recruiterId,
          companyId,
          name: recruiterName,
          dateFrom: weekStart.toISOString(),
          dateTo: addDays(weekStart, 7).toISOString(),
        });
        if (cancelled) return;
        const homeEvents = Array.isArray(data.events) ? data.events : [];
        setRecruiter(data.recruiter || null);
        // Same times as GetGeek calendar_events (backend also realigns MeetGeek rooms).
        setEvents(homeEvents);
        const resolved = String(data.recruiter?.name || '').trim();
        if (resolved && typeof onResolvedName === 'function') {
          onResolvedName(resolved);
        }
      } catch (err) {
        if (cancelled) return;
        setRecruiter(null);
        setEvents([]);
        setError(err.message || 'Failed to load calendar');
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    load();
    return () => {
      cancelled = true;
    };
  }, [canFetch, recruiterId, companyId, recruiterName, weekStart, refreshKey]); // onResolvedName omitted: parent passes stable/inline setter; avoid refetch loops

  useEffect(() => {
    if (!menuOpen && !calendarOpen) return undefined;
    const onPointerDown = (event) => {
      const target = event.target;
      if (menuOpen) {
        const insideMenu = topMenuRef.current?.contains(target) || bottomMenuRef.current?.contains(target);
        if (!insideMenu) setMenuOpen(false);
      }
      if (calendarOpen && calendarRef.current && !calendarRef.current.contains(target)) {
        setCalendarOpen(false);
      }
    };
    document.addEventListener('mousedown', onPointerDown);
    return () => document.removeEventListener('mousedown', onPointerDown);
  }, [menuOpen, calendarOpen]);

  const dayEvents = events
    .filter((event) => event.start && sameDay(new Date(event.start), selectedDate))
    .sort((a, b) => new Date(a.start) - new Date(b.start));

  const accountEmail = recruiter?.email || '';
  const knownName = recruiter?.name || recruiterName || '';
  const isToday = sameDay(selectedDate, today);
  const calendarDays = monthCells(visibleMonth);

  const selectDate = (date) => {
    const day = startOfDay(date);
    setSelectedDate(day);
    setWeekStart(startOfWeek(day));
    setCalendarOpen(false);
  };

  const shiftWeek = (direction) => {
    const nextWeek = addDays(weekStart, direction * 7);
    setWeekStart(nextWeek);
    setSelectedDate(nextWeek);
  };

  const goToToday = () => {
    selectDate(new Date());
  };

  const toggleMenu = (anchor) => {
    setCalendarOpen(false);
    setMenuAnchor(anchor);
    setMenuOpen((open) => (menuAnchor === anchor ? !open : true));
  };

  const openCalendar = () => {
    setMenuOpen(false);
    setVisibleMonth(startOfMonth(selectedDate));
    setCalendarOpen((open) => !open);
  };

  const handleStartInstant = () => {
    const name = knownName || draftName.trim();
    if (!name) return;
    setMenuOpen(false);
    onStartInstant(name);
  };

  // MeetGeek home is embedded from GetGeek with recruiterId + companyId.
  // Direct visits are gated for now; full landing below kept for future standalone.
  if (!canFetch) {
    return (
      <div className="mg-landing">
        <div className="mg-landing-top">
          <div className="mg-landing-brand">
            <MeetGeekLogo className="mg-meetgeek-logo" />
          </div>
        </div>
        <div className="mg-access-gate" role="status">
          <div className="mg-access-gate-card">
            <EmptyIllustration />
            <h1>Invalid or incomplete link</h1>
            <p>Please review the URL, or open MeetGeek from GetGeek.</p>
          </div>
        </div>
      </div>
    );
  }

  const newMenu = (
    <div className={`mg-new-menu${menuAnchor === 'bottom' ? ' is-above' : ''}`} role="menu">
      {!knownName && (
        <label className="mg-name-fallback">
          <span>Your name</span>
          <input
            type="text"
            value={draftName}
            onChange={(event) => setDraftName(event.target.value)}
            placeholder="Enter your name"
          />
        </label>
      )}
      <button
        type="button"
        role="menuitem"
        onClick={() => {
          setMenuOpen(false);
          onScheduleLater();
        }}
      >
        <CalendarMonthOutlined sx={{ fontSize: 20, color: '#5f6368' }} />
        Create a meeting for later
      </button>
      <button type="button" role="menuitem" onClick={handleStartInstant}>
        <AddOutlined sx={{ fontSize: 20, color: '#5f6368' }} />
        Start an instant meeting
      </button>
    </div>
  );

  return (
    <div className="mg-landing">
      <div className="mg-landing-top">
        <div className="mg-landing-brand" aria-label="Meetgeek">
          <MeetGeekLogo size={32} />
        </div>
        <div className="mg-landing-actions" ref={topMenuRef}>
          <form
            className="mg-join-pill"
            onSubmit={(event) => {
              event.preventDefault();
              onJoin(knownName || draftName.trim());
            }}
          >
            <input
              type="text"
              value={joinCode}
              onChange={(event) => onJoinCodeChange(event.target.value)}
              placeholder="Enter a code or link"
              aria-label="Enter a code or link"
            />
            <button
              type="submit"
              className={`mg-join-btn${joinCode.trim() ? ' is-ready' : ''}`}
              disabled={isJoining}
            >
              {isJoining ? '…' : 'Join'}
            </button>
          </form>

          <button
            type="button"
            className="mg-new-btn"
            aria-expanded={menuOpen && menuAnchor === 'top'}
            aria-haspopup="menu"
            disabled={isCreatingMeeting}
            onClick={() => toggleMenu('top')}
          >
            <VideoIcon />
            <span>{isCreatingMeeting ? 'Starting…' : 'New'}</span>
          </button>

          {menuOpen && menuAnchor === 'top' && newMenu}
        </div>
      </div>

      <div className="mg-landing-body">
        <div className="mg-day-row">
          <div className="mg-day-heading" ref={calendarRef}>
            <div className="mg-day-title">
              <div className="mg-day-label">{formatDayHeading(selectedDate)}</div>
              <button
                type="button"
                className="mg-calendar-btn"
                aria-label="Open calendar"
                aria-expanded={calendarOpen}
                onClick={openCalendar}
              >
                <CalendarIcon />
              </button>
              <button type="button" className="mg-today-btn" onClick={goToToday}>
                Today
              </button>
            </div>
            {isPastDay && <div className="mg-day-past">Past</div>}
            {calendarOpen && (
              <div className="mg-calendar-pop">
                <div className="mg-calendar-head">
                  <span>{formatMonthHeading(visibleMonth)}</span>
                  <button type="button" className="mg-today-btn" onClick={goToToday}>
                    Today
                  </button>
                  <span>
                    <button type="button" aria-label="Previous month" onClick={() => setVisibleMonth((month) => addMonths(month, -1))}>
                      ‹
                    </button>
                    <button type="button" aria-label="Next month" onClick={() => setVisibleMonth((month) => addMonths(month, 1))}>
                      ›
                    </button>
                  </span>
                </div>
                <div className="mg-calendar-grid">
                  {['M', 'T', 'W', 'T', 'F', 'S', 'S'].map((label, index) => (
                    <span key={`${label}-${index}`} className="mg-calendar-dow">{label}</span>
                  ))}
                  {calendarDays.map((day) => {
                    const inMonth = day.getMonth() === visibleMonth.getMonth();
                    const selected = sameDay(day, selectedDate);
                    const dayIsToday = sameDay(day, today);
                    return (
                      <button
                        key={day.toISOString()}
                        type="button"
                        className={`mg-calendar-day${selected ? ' is-selected' : ''}${dayIsToday ? ' is-today' : ''}${inMonth ? '' : ' is-outside'}`}
                        onClick={() => selectDate(day)}
                      >
                        {day.getDate()}
                      </button>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
          <div className="mg-week">
            <button type="button" className="mg-week-arrow" onClick={() => shiftWeek(-1)} aria-label="Previous week">
              ‹
            </button>
            {weekDays.map((day) => {
              const selected = sameDay(day, selectedDate);
              return (
                <button
                  key={day.toISOString()}
                  type="button"
                  className={`mg-week-day${selected ? ' is-selected' : ''}`}
                  onClick={() => setSelectedDate(startOfDay(day))}
                >
                  <small>{WEEKDAYS[day.getDay()]}</small>
                  <strong>{day.getDate()}</strong>
                </button>
              );
            })}
            <button type="button" className="mg-week-arrow" onClick={() => shiftWeek(1)} aria-label="Next week">
              ›
            </button>
          </div>
        </div>

        {loading && (
          <div className="mg-loading" role="status" aria-live="polite">
            <span className="mg-loading-spinner" aria-hidden="true" />
            <p>Loading events…</p>
          </div>
        )}
        {!loading && error && <div className="mg-status is-error">{error}</div>}
        {!loading && !error && dayEvents.length === 0 && (
          <div className="mg-empty">
            <EmptyIllustration />
            <h2>{isToday ? 'No meetings scheduled for today' : 'No meetings scheduled'}</h2>
            <p>Schedule a meeting or enjoy the free time</p>
            <div className="mg-empty-actions" ref={bottomMenuRef}>
              <button
                type="button"
                className="mg-new-btn"
                aria-expanded={menuOpen && menuAnchor === 'bottom'}
                onClick={() => toggleMenu('bottom')}
              >
                <VideoIcon />
                <span>New</span>
              </button>
              {menuOpen && menuAnchor === 'bottom' && newMenu}
            </div>
          </div>
        )}
        {!loading && !error && dayEvents.length > 0 && (
          <div className="mg-event-list">
            {dayEvents.map((event) => {
              const timing = isPastDay
                ? 'past'
                : getEventTimingStatus(event, now);
              const canJoin =
                timing !== 'past' && Boolean(event.roomName || event.hostLink);
              const canViewHistory =
                timing === 'past' &&
                Boolean(event.roomName) &&
                typeof onOpenHistoryMeeting === 'function';
              const isInteractive = canJoin || canViewHistory;
              return (
                <button
                  key={event.id}
                  type="button"
                  className={`mg-event-card is-${timing}${isInteractive ? '' : ' is-disabled'}`}
                  disabled={!isInteractive}
                  title={
                    canViewHistory
                      ? 'View meeting summary and recording'
                      : timing === 'past'
                        ? 'This meeting has ended'
                        : canJoin
                          ? 'Join meeting'
                          : 'No meeting link'
                  }
                  onClick={() => {
                    if (canViewHistory) {
                      onOpenHistoryMeeting(event.roomName);
                      return;
                    }
                    if (!canJoin) return;
                    onJoinEvent(event, knownName || draftName.trim());
                  }}
                >
                  <span className="mg-event-main">
                    <span className="mg-event-meta">
                      <span className="mg-event-time">
                        {formatTimeRange(event.start, event.end)}
                      </span>
                      <span className={`mg-event-status is-${timing}`}>
                        {EVENT_STATUS_LABEL[timing]}
                      </span>
                    </span>
                    <span className="mg-event-title">{event.title}</span>
                  </span>
                  <span className="mg-event-detail">{event.detail}</span>
                </button>
              );
            })}
          </div>
        )}

      </div>

      <div className="mg-landing-wide">
        {accountEmail && (
          <div className="mg-landing-footer">
            <div>
              From your calendar account: <strong>{accountEmail}</strong>
            </div>
            <a
              className="mg-learn-more"
              href="https://www.getgeek.ai"
              target="_blank"
              rel="noreferrer"
            >
              Learn more about Meetgeek
            </a>
          </div>
        )}

        <RecentMeetingsSection
          companyId={companyId}
          recruiterId={recruiterId}
          onViewAll={onViewMeetingHistory}
          onOpenMeeting={onOpenHistoryMeeting}
        />
      </div>
    </div>
  );
}
